import { c as _c } from "react/compiler-runtime";
import { usePageView } from '@/analytics/pageTypes';
import { Layout } from '@/components/layout';
import IconLightBulb from '@/assets/icons/heroIcons/Light-bulb.svg';
import IconThird from '@/assets/icons/heroIcons/Icon-third.svg';
import IconMaxi from '@/assets/icons/heroIcons/Maxi.svg';
import Eshops from '@/sections/Eshops';
import MoreInfoSection from '@/sections/MoreInfo';
import { Testimonials as TestimonialsSection } from '@/sections/Testimonials';
import { Button } from '@/components/buttons';
import { Text } from '@/components/typography';
import HeroSimple from '@/sections/HeroSimple';
import { type IntlProps, getIntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { useTranslation } from 'react-i18next';
import IconCard from '@/assets/icons/heroIcons/Card-front.svg';
import IconDeferred from '@/assets/icons/heroIcons/Extend.svg';
import IconPayInThree from '@/assets/icons/heroIcons/Icon-third.svg';
import IconLoan from '@/assets/icons/heroIcons/Loan.svg';
import { LINKS } from '@/constants/links';
import React from 'react';
import IconDeferredPayment from '@/assets/icons/heroIcons/Deferred-payment.svg';
import { ProductBoxes } from '@/sections/ourProducts/ProductBoxes';
import { getTestimonialsBySlug, Testimonial } from '@/api/sanity/testimonials';
import { SeoHeader } from '@/components/SeoHeader';
import { type EshopsList, getAffiliateEshopsList } from '@/api/sanity/affiliateEshopsList';
import { AnimatedSection, AnimatedContent } from '@/components/animation';
import { isAllowedRoute } from '@/lang/pagesConfig';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import FilledComparisonTable from '@/sections/ourProducts/FilledComparisonTable';
import { FaqIntro } from '@/sections/FaqIntro';
import { BenefitTiles } from '@/components/tiles';
import { IconTile } from '../components/IconTile';
import useMediaQuery from '@/hooks/useMediaQuery';
import { getSectionAnchorId } from '@/utils/anchors';
import { getMediaQuery } from '@/styles/sizes';
interface Props extends IntlProps {
  eshops: EshopsList;
  testimonials: Testimonial[];
  menuPromoBanners?: PageMenuPromoBanner[];
}
export default function Page(props) {
  const $ = _c(140);
  usePageView("products");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["ourProducts", "core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const {
    eshops,
    testimonials,
    menuPromoBanners
  } = props;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getMediaQuery("lg");
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isDesktop = useMediaQuery(t1);
  const productsPackagesRef = React.useRef(null);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (productsPackagesRef.current) {
        productsPackagesRef.current.scrollIntoView({
          behavior: "smooth"
        });
      }
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleScrollToProductPackages = t2;
  let t3;
  if ($[3] !== t) {
    t3 = t("ourProducts:seo.title");
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("ourProducts:seo.description");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <SeoHeader title={t3} description={t4} />;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t) {
    t6 = t("banner.title");
    $[10] = t;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== isDesktop) {
    t7 = isDesktop ? {} : {
      withoutCharacters: true
    };
    $[12] = isDesktop;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t) {
    t8 = t("banner.text");
    $[14] = t;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t8) {
    t9 = <Text variant="p1" className="mx-auto max-w-142 text-center">{t8}</Text>;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <IconTile icon={<IconDeferred />} />;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t) {
    t11 = t("productsTiles.deferredPayment.title");
    $[19] = t;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t) {
    t12 = t("productsTiles.deferredPayment.text");
    $[21] = t;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = LINKS();
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t11 || $[25] !== t12) {
    t14 = {
      icon: t10,
      title: t11,
      subtitle: t12,
      to: t13.deferredPayment,
      analyticsParam: "ourProductsDeferredPayment",
      testId: "ourProductsDeferredPayment"
    };
    $[24] = t11;
    $[25] = t12;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <IconTile icon={<IconPayInThree />} />;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t) {
    t16 = t("productsTiles.payInThree.title");
    $[28] = t;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t) {
    t17 = t("productsTiles.payInThree.text");
    $[30] = t;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = LINKS();
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t16 || $[34] !== t17) {
    t19 = {
      icon: t15,
      title: t16,
      subtitle: t17,
      to: t18.payInThree,
      analyticsParam: "ourProductsPayInThree",
      testId: "ourProductsPayInThree"
    };
    $[33] = t16;
    $[34] = t17;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <IconTile icon={<IconCard />} />;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== t) {
    t21 = t("productsTiles.card.title");
    $[37] = t;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== t) {
    t22 = t("productsTiles.card.text");
    $[39] = t;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = LINKS();
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== t21 || $[43] !== t22) {
    t24 = {
      icon: t20,
      title: t21,
      subtitle: t22,
      to: t23.card,
      analyticsParam: "ourProductsCard",
      testId: "ourProductsCard"
    };
    $[42] = t21;
    $[43] = t22;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <IconTile icon={<IconLoan />} />;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  let t26;
  if ($[46] !== t) {
    t26 = t("productsTiles.loan.title");
    $[46] = t;
    $[47] = t26;
  } else {
    t26 = $[47];
  }
  let t27;
  if ($[48] !== t) {
    t27 = t("productsTiles.loan.text");
    $[48] = t;
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  let t28;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = LINKS();
    $[50] = t28;
  } else {
    t28 = $[50];
  }
  let t29;
  if ($[51] !== t26 || $[52] !== t27) {
    t29 = {
      icon: t25,
      title: t26,
      subtitle: t27,
      to: t28.loan,
      analyticsParam: "ourProductsLoan",
      testId: "ourProductsLoan"
    };
    $[51] = t26;
    $[52] = t27;
    $[53] = t29;
  } else {
    t29 = $[53];
  }
  let t30;
  if ($[54] !== t14 || $[55] !== t19 || $[56] !== t24 || $[57] !== t29) {
    t30 = <BenefitTiles className="mt-14" height="large" items={[t14, t19, t24, t29]} />;
    $[54] = t14;
    $[55] = t19;
    $[56] = t24;
    $[57] = t29;
    $[58] = t30;
  } else {
    t30 = $[58];
  }
  let t31;
  if ($[59] !== t) {
    t31 = t("banner.cta");
    $[59] = t;
    $[60] = t31;
  } else {
    t31 = $[60];
  }
  let t32;
  if ($[61] !== t31) {
    t32 = <Button onClick={handleScrollToProductPackages} testId="ourProductsScrollToPackages" analyticsParam="ourProductsScrollToPackages" size="lg" variant="primary" align="center" className="mt-12 md:mt-16">{t31}</Button>;
    $[61] = t31;
    $[62] = t32;
  } else {
    t32 = $[62];
  }
  let t33;
  if ($[63] !== t30 || $[64] !== t32 || $[65] !== t6 || $[66] !== t7 || $[67] !== t9) {
    t33 = <HeroSimple title={t6} titleClassName="mb-4 lg:mb-8" circleClassName="bgCircle absolute right-0 top-0 left-auto hidden h-[50rem] w-[50rem] -translate-y-1/4! translate-x-1/3! rotate-180 lg:block" className="bg-blue-100" {...t7}>{t9}{t30}{t32}</HeroSimple>;
    $[63] = t30;
    $[64] = t32;
    $[65] = t6;
    $[66] = t7;
    $[67] = t9;
    $[68] = t33;
  } else {
    t33 = $[68];
  }
  let t34;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = getSectionAnchorId("productBoxes");
    $[69] = t34;
  } else {
    t34 = $[69];
  }
  let t35;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <ProductBoxes />;
    $[70] = t35;
  } else {
    t35 = $[70];
  }
  let t36;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = LINKS();
    $[71] = t36;
  } else {
    t36 = $[71];
  }
  const t37 = t36.registration;
  let t38;
  if ($[72] !== t) {
    t38 = t("products.cta");
    $[72] = t;
    $[73] = t38;
  } else {
    t38 = $[73];
  }
  let t39;
  if ($[74] !== t38) {
    t39 = <div ref={productsPackagesRef}><AnimatedSection bgColor="bg-blue-900" id={t34}><AnimatedContent className="pb-10 lg:pb-20">{t35}<Button to={t37} testId="ourProductsRegistration" analyticsParam="ourProductsRegistration" size="lg" variant="primary" align="center" className="mt-12 md:mt-16">{t38}</Button></AnimatedContent></AnimatedSection></div>;
    $[74] = t38;
    $[75] = t39;
  } else {
    t39 = $[75];
  }
  let t40;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = getSectionAnchorId("comparisonTable");
    $[76] = t40;
  } else {
    t40 = $[76];
  }
  let t41;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <AnimatedSection bgColor="bg-blue-100" id={t40}><AnimatedContent className="pb-10 lg:pb-20"><FilledComparisonTable /></AnimatedContent></AnimatedSection>;
    $[77] = t41;
  } else {
    t41 = $[77];
  }
  let t42;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = getSectionAnchorId("testimonials");
    $[78] = t42;
  } else {
    t42 = $[78];
  }
  let t43;
  if ($[79] !== testimonials) {
    t43 = <AnimatedContent className="relative z-10 pb-14 lg:pb-20"><TestimonialsSection testimonials={testimonials} /></AnimatedContent>;
    $[79] = testimonials;
    $[80] = t43;
  } else {
    t43 = $[80];
  }
  let t44;
  if ($[81] !== eshops) {
    t44 = eshops.length > 0 && <AnimatedContent className="pt-20"><Eshops eshops={eshops} /></AnimatedContent>;
    $[81] = eshops;
    $[82] = t44;
  } else {
    t44 = $[82];
  }
  let t45;
  if ($[83] !== t43 || $[84] !== t44) {
    t45 = <AnimatedSection bgColor="bg-blue-50" id={t42}>{t43}{t44}</AnimatedSection>;
    $[83] = t43;
    $[84] = t44;
    $[85] = t45;
  } else {
    t45 = $[85];
  }
  let t46;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = getSectionAnchorId("moreInfo");
    $[86] = t46;
  } else {
    t46 = $[86];
  }
  let t47;
  if ($[87] !== t) {
    t47 = t("ourProducts:faq.title");
    $[87] = t;
    $[88] = t47;
  } else {
    t47 = $[88];
  }
  let t48;
  if ($[89] !== t) {
    t48 = t("ourProducts:faq.text");
    $[89] = t;
    $[90] = t48;
  } else {
    t48 = $[90];
  }
  let t49;
  if ($[91] !== t) {
    t49 = t("ourProducts:faq.cta");
    $[91] = t;
    $[92] = t49;
  } else {
    t49 = $[92];
  }
  let t50;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = LINKS();
    $[93] = t50;
  } else {
    t50 = $[93];
  }
  let t51;
  if ($[94] !== t47 || $[95] !== t48 || $[96] !== t49) {
    t51 = <AnimatedContent className="relative z-10 py-14 lg:py-20"><FaqIntro title={t47} text={t48} cta={t49} link={t50.registration} /></AnimatedContent>;
    $[94] = t47;
    $[95] = t48;
    $[96] = t49;
    $[97] = t51;
  } else {
    t51 = $[97];
  }
  let t52;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <IconDeferredPayment className="fillButtonIcon" />;
    $[98] = t52;
  } else {
    t52 = $[98];
  }
  let t53;
  if ($[99] !== t) {
    t53 = t("core:products.deferredPayment");
    $[99] = t;
    $[100] = t53;
  } else {
    t53 = $[100];
  }
  let t54;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = LINKS();
    $[101] = t54;
  } else {
    t54 = $[101];
  }
  let t55;
  if ($[102] !== t53) {
    t55 = {
      analyticsParam: "ourProductsMorDeferredPayment",
      icon: t52,
      testId: "ourProductsMoreDeferredPayment",
      title: t53,
      to: t54.deferredPayment
    };
    $[102] = t53;
    $[103] = t55;
  } else {
    t55 = $[103];
  }
  let t56;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t56 = <IconMaxi className="fillButtonIcon" />;
    $[104] = t56;
  } else {
    t56 = $[104];
  }
  let t57;
  if ($[105] !== t) {
    t57 = t("core:products.revolving");
    $[105] = t;
    $[106] = t57;
  } else {
    t57 = $[106];
  }
  let t58;
  if ($[107] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = LINKS();
    $[107] = t58;
  } else {
    t58 = $[107];
  }
  let t59;
  if ($[108] !== t57) {
    t59 = {
      analyticsParam: "ourProductsMoreInfoRevolvingApplication",
      icon: t56,
      testId: "ourProductsMoreInfoRevolvingApplication",
      title: t57,
      to: t58.revolving
    };
    $[108] = t57;
    $[109] = t59;
  } else {
    t59 = $[109];
  }
  let t60;
  if ($[110] === Symbol.for("react.memo_cache_sentinel")) {
    t60 = <IconThird className="fillButtonIcon" />;
    $[110] = t60;
  } else {
    t60 = $[110];
  }
  let t61;
  if ($[111] !== t) {
    t61 = t("core:link.payInThree");
    $[111] = t;
    $[112] = t61;
  } else {
    t61 = $[112];
  }
  let t62;
  if ($[113] === Symbol.for("react.memo_cache_sentinel")) {
    t62 = LINKS();
    $[113] = t62;
  } else {
    t62 = $[113];
  }
  let t63;
  if ($[114] !== t61) {
    t63 = {
      analyticsParam: "ourProductsMoreInfoPayInThree",
      icon: t60,
      testId: "ourProductsMoreInfoPayInThree",
      title: t61,
      to: t62.payInThree
    };
    $[114] = t61;
    $[115] = t63;
  } else {
    t63 = $[115];
  }
  let t64;
  if ($[116] === Symbol.for("react.memo_cache_sentinel")) {
    t64 = <IconLightBulb className="fillButtonIcon" />;
    $[116] = t64;
  } else {
    t64 = $[116];
  }
  let t65;
  if ($[117] !== t) {
    t65 = t("core:link.shopWisely");
    $[117] = t;
    $[118] = t65;
  } else {
    t65 = $[118];
  }
  let t66;
  if ($[119] === Symbol.for("react.memo_cache_sentinel")) {
    t66 = LINKS();
    $[119] = t66;
  } else {
    t66 = $[119];
  }
  let t67;
  if ($[120] !== t65) {
    t67 = {
      analyticsParam: "ourProductsMoreInfoShopWisely",
      icon: t64,
      testId: "ourProductsMoreInfoShopWisely",
      title: t65,
      to: t66.shopWisely
    };
    $[120] = t65;
    $[121] = t67;
  } else {
    t67 = $[121];
  }
  let t68;
  if ($[122] !== t55 || $[123] !== t59 || $[124] !== t63 || $[125] !== t67) {
    t68 = <AnimatedContent className="relative z-10"><MoreInfoSection buttons={[t55, t59, t63, t67]} /></AnimatedContent>;
    $[122] = t55;
    $[123] = t59;
    $[124] = t63;
    $[125] = t67;
    $[126] = t68;
  } else {
    t68 = $[126];
  }
  let t69;
  let t70;
  let t71;
  if ($[127] === Symbol.for("react.memo_cache_sentinel")) {
    t69 = <div className="bgCircle absolute top-1/4 right-0 translate-x-1/3 rotate-270 md:hidden" />;
    t70 = <div className="bgCircle absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 rotate-180" />;
    t71 = <div className="bgCircle absolute top-0 left-0 -translate-x-1/2 translate-y-1/4 rotate-180" />;
    $[127] = t69;
    $[128] = t70;
    $[129] = t71;
  } else {
    t69 = $[127];
    t70 = $[128];
    t71 = $[129];
  }
  let t72;
  if ($[130] !== t51 || $[131] !== t68) {
    t72 = <AnimatedSection bgColor="bg-blue-100" className="pb-0!" id={t46}><div className="relative overflow-hidden">{t51}{t68}{t69}{t70}{t71}</div></AnimatedSection>;
    $[130] = t51;
    $[131] = t68;
    $[132] = t72;
  } else {
    t72 = $[132];
  }
  let t73;
  if ($[133] !== menuPromoBanners || $[134] !== t33 || $[135] !== t39 || $[136] !== t45 || $[137] !== t5 || $[138] !== t72) {
    t73 = <Layout menuPromoBanners={menuPromoBanners} className="bg-blue-100">{t5}{t33}{t39}{t41}{t45}{t72}</Layout>;
    $[133] = menuPromoBanners;
    $[134] = t33;
    $[135] = t39;
    $[136] = t45;
    $[137] = t5;
    $[138] = t72;
    $[139] = t73;
  } else {
    t73 = $[139];
  }
  return t73;
}
export const getStaticProps = (async ctx => {
  const isAllowed = isAllowedRoute('nase-produkty');
  if (!isAllowed) {
    return {
      notFound: true
    };
  }
  return {
    props: {
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      eshops: await getAffiliateEshopsList('products'),
      testimonials: await getTestimonialsBySlug('products'),
      ...(await getIntlProps(ctx, ['core', 'eshops', 'testimonials', 'ourProducts']))
    }
  };
}) satisfies GetStaticProps<Props>;